import { useState, useCallback, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { getAllArticles as getAllArticlesLocal, getAllCategories as getAllCategoriesLocal } from "../blog";
import { getAllArticles as getAllArticlesSanity } from "../services/sanityQueries";
import { useTranslation } from "react-i18next";
import { buildBlogListJsonLd } from "../utils/blogSeo";
import { BLOG_CATEGORY_ALL, SITE_URL } from "../constants/blog";
import { useBlogSearch } from "../hooks/useBlogSearch";
import { useBlogFilter } from "../hooks/useBlogFilter";
import { useIntersectionReveal } from "../hooks/useIntersectionReveal";
import { WA_URL } from "../config/constants";
import { normalizeSanityArticle } from "../utils/sanityNormalizer";

import BlogCard from "../components/blog/BlogCard";
import BlogErrorBoundary from "../components/blog/BlogErrorBoundary";
import EmptyState from "../components/ui/EmptyState";

import "./Blog.css";

/**
 * /blog — Blog listing page.
 * Fetches from Sanity CMS; falls back to local static articles if Sanity fails.
 */
export default function Blog() {
  const [articles,   setArticles]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [query,      setQuery]      = useState("");
  const [category,   setCategory]   = useState(BLOG_CATEGORY_ALL);
  const hasFetched = useRef(false);
  const { t } = useTranslation("blog");

  useIntersectionReveal();

  // ── Fetch from Sanity ──────────────────────────────────────────────────────
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const controller = new AbortController();

    getAllArticlesSanity()
      .then((raw) => {
        if (controller.signal.aborted) return;
        if (!raw || raw.length === 0) return;
        const normalized = raw.map(normalizeSanityArticle);
        const cats = [...new Set(normalized.map((a) => a.category).filter(Boolean))];
        setArticles(normalized);
        setCategories(cats);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        const fallback = getAllArticlesLocal();
        const cats = [...new Set(fallback.map((a) => a.category).filter(Boolean))];
        setArticles(fallback);
        setCategories(cats);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  // ── Composed pipeline: search → filter ────────────────────────────────────
  const { results: searched } = useBlogSearch(articles, query);
  const { results: filtered } = useBlogFilter(searched, category);

  const featured      = articles.find((a) => a.featured);
  const isDefaultView = !query && category === BLOG_CATEGORY_ALL;
  const gridArticles  = isDefaultView
    ? filtered.filter((a) => a.slug !== featured?.slug)
    : filtered;

  const jsonLd = buildBlogListJsonLd(articles);

  const handleQueryChange = useCallback((e) => setQuery(e.target.value), []);
  const handleClearSearch = useCallback(() => {
    setQuery("");
    setCategory(BLOG_CATEGORY_ALL);
  }, []);

  return (
    <BlogErrorBoundary>
      <main className="blog-page">
        <Helmet>
          <title>Dubai Real Estate Blog — ProNet Real Estate</title>
          <meta
            name="description"
            content="Expert insights on the Dubai property market, Golden Visa, investment tips, and the best areas to buy in 2026. Updated by ProNet's senior consultants."
          />
          <link rel="canonical" href={`${SITE_URL}/blog`} />
          <meta property="og:title"       content="Dubai Real Estate Blog — ProNet Real Estate" />
          <meta property="og:description" content="Expert insights on Dubai property investment, Golden Visa, and market trends." />
          <meta property="og:url"         content={`${SITE_URL}/blog`} />
          <meta property="og:type"        content="website" />
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>

        {/* ── HERO ─────────────────────────────────────────────────────────── */}
        <section className="blog-hero" aria-labelledby="blog-hero-title">
          <div className="container">
            <div className="section-eyebrow" aria-hidden="true">
              <span className="eyebrow-line" />
              <span>{t("hero.eyebrow")}</span>
            </div>
            <h1 id="blog-hero-title" className="blog-hero-title">
              {t("hero.title0")} <em>{t("hero.title1")}</em>
            </h1>
            <p className="blog-hero-sub">{t("hero.sub")}</p>
          </div>
        </section>

        {/* ── FEATURED ─────────────────────────────────────────────────────── */}
        {!loading && featured && isDefaultView && (
          <section className="blog-featured container" aria-label={t("featured")}>
            <BlogCard article={featured} featured />
          </section>
        )}

        {/* ── FILTERS ──────────────────────────────────────────────────────── */}
        <section
          className="blog-filters container"
          aria-label="Search and filter articles"
        >
          <div className="blog-search-wrap">
            <svg
              className="blog-search-icon"
              width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="1.5"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M16.5 16.5L21 21" strokeLinecap="round" />
            </svg>
            <label htmlFor="blog-search" className="sr-only">{t("search.label")}</label>
            <input
              id="blog-search"
              type="search"
              className="blog-search"
              placeholder={t("search.placeholder")}
              value={query}
              onChange={handleQueryChange}
              aria-label={t("search.label")}
              autoComplete="off"
            />
          </div>

          <div className="blog-cats" role="group" aria-label={t("filter.label")}>
            {[BLOG_CATEGORY_ALL, ...categories].map((c) => (
              <button
                key={c}
                className={`blog-cat-btn ${category === c ? "active" : ""}`}
                onClick={() => setCategory(c)}
                aria-pressed={category === c}
              >
                {c === BLOG_CATEGORY_ALL ? t("filter.all") : c}
              </button>
            ))}
          </div>
        </section>

        {/* ── ARTICLE GRID ─────────────────────────────────────────────────── */}
        <section
          className="blog-grid-section container"
          aria-label="Articles"
          aria-live="polite"
        >
          {loading ? (
            <div className="blog-loading" aria-live="polite" aria-label={t("loading")}>
              <div className="blog-loading-spinner" aria-hidden="true" />
              <p>{t("loading")}</p>
            </div>
          ) : gridArticles.length === 0 && !(featured && isDefaultView) ? (
            <EmptyState
              icon="🔍"
              title={t("search.noResults")}
              message={t("search.noResultsSub")}
              action={{ label: t("search.clearFilters"), onClick: handleClearSearch }}
            />
          ) : (
            <div className="blog-grid" role="list">
              {gridArticles.map((a) => (
                <div key={a.slug} role="listitem">
                  <BlogCard article={a} />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────────── */}
        <section className="blog-cta-sec reveal" aria-labelledby="blog-cta-title">
          <div className="container">
            <div className="blog-cta-inner">
              <div className="blog-cta-text">
                <div className="section-eyebrow" aria-hidden="true">
                  <span className="eyebrow-line" />
                  <span>{t("cta.eyebrow")}</span>
                </div>
                <h2 id="blog-cta-title">
                  {t("cta.title0")} <em>{t("cta.title1")}</em>
                </h2>
                <p>{t("cta.sub")}</p>
              </div>
              <div className="blog-cta-btns">
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-gold"
                  aria-label="Contact us on WhatsApp"
                >
                  <span>{t("cta.whatsapp")}</span>
                </a>
                <Link to="/contact" className="btn-outline">
                  {t("cta.message")}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </BlogErrorBoundary>
  );
}
